import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import cx from "classnames";
import _ from "lodash";
import {
  Container, Button, Form, Dropdown, Offcanvas
} from 'react-bootstrap';
import { useStoreApi } from '@xyflow/react';
import { createJourneyStep, updateJourneyStep } from "actions/journey";
import { useMemo } from "react";
import { filterOptions, orderOptions, sortOptions } from "constants/filters";
import { guid } from "services/util";
import { fetchAssetMappings, updateFilterSortInContentManager } from "actions/content";
import { AssetFilter, PropertyContext } from "components/AssetFilter";
import { Loader } from "components/ApplicationLoader";
import InfiniteScroll from "components/InfiniteScroll";
import { processRequest } from "services/filters.service";
import { ContentCard } from "./components";
import { Footer } from "../DynamicManager/components";
import styles from "./manualManager.module.scss";

const ManualManager = ({show, onClose, journeyId, selectedNode, className}) => {
  const dispatch = useDispatch();
  const container = React.createRef(null);
  const store = useStoreApi();
  const { nodes } = store.getState();

  const [selectAll, setSelectAll] = useState(false);
  const [allowType,setAllowType] = useState(null)
  const [state, setState] = useState({
    view: '',
    selection: []
  })
  const selection = _.get(state, 'selection', []) || [];
  const inline = false;
  const multiple = false;

  const storyboard = useSelector((state) => _.get(state, `storyboards.storyboard.item`, {}));

  const contents = useSelector(state => _.get(state, 'content.assets')) || [];
  const contentUpdated = useSelector(state => _.get(state, 'content.updated', false));
  const loading = useSelector(state => _.get(state, 'content.loading', false));
  const pagination = useSelector(state => _.get(state, 'content.pagination', {}));
  const hasMoreItems = _.get(pagination, "currentPage") < _.get(pagination, "totalPages") - 1;

  const filters = useSelector(state => _.get(state, `content.assetFilters.filters`));
  const sort = useSelector(state => _.get(state, `content.assetFilters.sort`, 'modified_date'));
  const order = useSelector(state => _.get(state, `content.assetFilters.order`, 'DESC'));

  const empty = _.size(contents) === 0;
  const steps = useSelector(state => _.get(state, `journey.${journeyId}.steps`) || []);
  const content = _.find(steps, function (step) {
    return step.uid == selectedNode || step.id == selectedNode ;
  });
  const existingNode = _.some(steps, function (step) {
    return step.id === selectedNode;
  });
  const mapping = _.get(content, 'mapping') || null;

  const stepDetails = _.find(nodes, { id: selectedNode });
  const stepDetailsFromSteps = _.find(steps, { id: selectedNode });
  
  const isTriggerNode = stepDetails?.parentNode === undefined ? stepDetailsFromSteps?.start ?? true : false;
  
  useEffect(()=> {
    if (mapping) {
      onSelectionChange([mapping]);
    } else {
      onSelectionChange([]);
    }
  },[show]);

  useEffect(() => {
    handleFetchMappings(0);
  }, [filters, sort, order]);

  useEffect(() => {
    if (!contentUpdated) {
      return
    }
    onSelectionChange([])
  }, [contentUpdated]);

  useEffect(() => {
    if (selectAll) {
      onSelectionChange(contents)
    }
  }, [contents, selectAll]);

  const handleFetchMappings = (page) => {
    const requestId = guid();
    
    var commandsRequest = processRequest(filters);
    commandsRequest = (commandsRequest || []).filter(item => item.operator === "IS_ALL" || item.value && item.value.length > 0);

    if(storyboard) {
      var request = {
        storyboard: _.get(storyboard, 'id'),
        sort: sort,
        order: order,
        excludeStoryboardAssets: inline
      }

      if(commandsRequest.length > 0){
        request = {
          ...request,
          commands: commandsRequest
        }
      }

      dispatch(fetchAssetMappings(request, page, 20, requestId));
    }
  }

  const handleAssetClicked = (e, asset) => {

    var multiSelect = false;
    if ((e.metaKey || e.shiftKey || e.ctrlKey) && multiple) {
      multiSelect = true;
    }

    var updated = selection || [];

    var isSelected = updated.some(item => item.id === asset.id);

    if (isSelected && !multiSelect) {
      return;
    }

    if (!isSelected) {
      if (multiSelect) {
        updated = [...updated, asset];
      } else {
        updated = [asset];
      }
    }

    if (isSelected && multiSelect && updated.length > 1) {
      updated = updated.filter(item => item.id !== asset.id);
    }

    onSelectionChange(updated);
  }

  const loadMore = () => {
    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }
    handleFetchMappings(nextPage);
  }

  const onSelectionChange = (value) => {
    setState((state) => {
      return {
        ...state,
        selection: value
      }
    });
  }

  const onFinish =()=> {
    const stepDetails = _.find(nodes, (node)=>{return node.id === selectedNode});

    var contentStepRequest = {
      id: journeyId,
      mapping: selection[0].id,
      type: "CONTENT",
      mappingType: 'MANUAL',
      start: (stepDetails && !stepDetails.parentNode) ? true : "",
    }

    if (!mapping && !existingNode ) {
      dispatch(createJourneyStep(journeyId, selectedNode, contentStepRequest));
    } else {
      dispatch(updateJourneyStep(journeyId, selectedNode, contentStepRequest));
    }

    onClose()
  }


  return (
    <Offcanvas 
      className={styles.chooseAssetPanel} 
      show={show} onHide={onClose} 
      backdropClassName={styles.backdropFigmaContentType} 
      placement='end' 
      backdrop="static"
    > 
      <div className={styles.assetContentContainer}>
        <Button className={styles.slideBackButton} variant="info" onClick={onClose} >
          <i className={cx("cleverstory-close")} />
        </Button>
        <header className={styles.header}>
            <h1 className={styles.title}>
              {isTriggerNode ? 'Manual Trigger' : 'Recommend'}</h1>
            <span className={styles.description}>
              <i className={'cleverstory-help'} />
              {isTriggerNode ? "Select one or more assets for multiple trigger." : "Guide users to the next best content piece."}
            </span>
        </header>
        <Container fluid className={cx(styles.dynamicContainer, className)}>
          <section className={styles.assetGridLayout}>
            <FilterRenderer />
            <div className={styles.scrollableContainer} ref={container}>
            {(empty && !loading) && renderEmptyMessage()}
            {loading && contents.length === 0 &&
              <div className={styles.loadingAnimationContainer}>
                <Loader className={styles.loadingAnimation} title="Loading..." />
              </div>
            }
            {contents.length > 0 &&
              <InfiniteScroll
                className={cx(styles.scrollable)}
                loading={loading}
                hasMore={hasMoreItems}
                loadMore={loadMore}
              >
                {contents.filter(asset => _.get(asset, 'asset.entityType') === 'asset').map((asset, index) => {
                  const selected = selection?.some(item => item.id === _.get(asset, 'id', null));
                  return (
                    <ContentCard
                      key={index}
                      id={_.get(asset, 'id', null)}
                      assetMapping={asset}
                      selected={selected}
                      onClick={handleAssetClicked}
                      allowType={allowType}
                      journey={true}
                    />
                  );
                })}
              </InfiniteScroll>
            }
          </div>
          </section>
        </Container>
        <Footer 
          type={'manual'} 
          selected={selection?.length}
          updating={false} 
          onSave={onFinish}
          cancel={onClose} 
        />
      </div>
    </Offcanvas>

  );
};

const FilterRenderer = ({ inline }) => {
  const dispatch = useDispatch();

  const filters = useSelector(state => _.get(state, `content.assetFilters.filters`) || []);
  const sort = useSelector(state => _.get(state, `content.assetFilters.sort`, 'modified_date'));
  const order = useSelector(state => _.get(state, `content.assetFilters.order`, 'DESC'));

  const customFields = useSelector(state => _.get(state, 'fields.items', []));

  const propertiesOption = useMemo(() => {
    const customFieldProperties = customFields.map(field => {
      return {
        key: `custom_field_${field.id}`,
        value: {
          fieldId: field.id,
          type: 'CUSTOM_FIELD'
        },
        label: field.name,
        data_type: 'list',
        icon: 'list-field',
        config: {
          multiple: true,
          options: (field.options || []).map(option => {
            return {
              value: option.id,
              label: option.value
            }
          })
        }
      }
    })

    return [...filterOptions, ...customFieldProperties];
  })

  useEffect(() => {
    if (filters && filters.length === 0) {
      initiateLayout();
    }
  }, [])

  const initiateLayout = () => {
    var requestFilters = filters || [];

    if (!filters || _.size(filters) <= 0) {
      requestFilters = [...filters, {
        property: {
          type: 'ASSET'
        },
        operator: 'IS'
      }];
    }

    requestFilters = requestFilters.map(item => {
      return {
        ...item,
        id: guid()
      }
    })

    var request = {
      ...filters,
      filters: requestFilters,
      sort: sort,
      order: order
    }

    dispatch(updateFilterSortInContentManager(request));
  }

  const updateFilterChanges = (filterRequests) => {
    var request = {
      filters: filterRequests
    }

    dispatch(updateFilterSortInContentManager(request));
  }

  const insertFilter = () => {
    var request = {
      id: guid(),
      property: {
        type: 'ASSET'
      },
      operator: 'IS'
    }

    var requestFilters = [...filters, request];

    updateFilterChanges(requestFilters);
  }

  const deleteFilter = (index) => {
    var updated = [...filters];
    updated.splice(index, 1);

    updateFilterChanges(updated);
  }

  const onFilterChange = (filter) => {
    var updated = filters.map((item, index) => {
      if (item.id === filter.id) {
        return filter;
      }

      return item;
    });

    updateFilterChanges(updated);
  }

  return (
    <Container fluid className={styles.dynamicFilterContainer}>
      <div className={styles.filterHeading}>Search</div>
      <PropertyContext.Provider value={propertiesOption}>
        {filters.map((data, index) => {
          return (
            <AssetFilter
              key={index}
              index={index}
              value={data}
              onChange={onFilterChange}
              onInsert={insertFilter}
              onDelete={deleteFilter}
              canDelete={index !== 0}
              showAddFilter={index === (filters.length - 1)}
              filters={filters}
              filterFor="journey"
            />
          )
        })}
      </PropertyContext.Provider>
      <SortRender />
    </Container>
  )
}

const SortRender = () => {
  const dispatch = useDispatch();

  const sort = useSelector(state => _.get(state, `content.assetFilters.sort`, 'modified_date'));
  const order = useSelector(state => _.get(state, `content.assetFilters.order`, 'DESC'));

  const sortOption = sortOptions.find(item => item.value === sort);
  const orderOption = orderOptions.find(item => item.value === order);

  const handleSortChange = (sort) => {
    var request = {
      sort: sort
    }
    dispatch(updateFilterSortInContentManager(request));
  }

  const handleOrderChange = (order) => {
    var request = {
      order: order
    }
    dispatch(updateFilterSortInContentManager(request));
  }

  const handleOrderKeyDown = (event, data) => {
    if (event.key === 'Enter') {
      handleOrderChange(data);
    }
  };

  return (
    <div className={styles.sortActionContainer}>
      <div className={styles.conditions}>

        <span className={styles.label}>
          <i className={cx(styles.sortIcon, 'cleverstory-sort')} />
          Sort
        </span>

        <Dropdown className={styles.sortDropdown} onSelect={handleSortChange}>
          <Dropdown.Toggle className={styles.selected}>
            <Button size='sm' variant='primary' className={styles.button}>
              <span className={styles.filterType}>
                {_.get(sortOption, 'label', 'Modified Date')}
              </span>
              <i className={cx(styles.icon, 'cleverstory-drop-down')} />
            </Button>
          </Dropdown.Toggle>
          <Dropdown.Menu className={styles.typesMenu}>
            {sortOptions.map(item => {
              return <Dropdown.Item
                eventKey={item.value}
                onChange={() => handleSortChange(item.value)}>
                {item.label}
              </Dropdown.Item>
            })}
          </Dropdown.Menu>
        </Dropdown>

        <Dropdown className={styles.sortDropdown} onSelect={handleOrderChange}>
          <Dropdown.Toggle className={styles.selected}>
            <Button size='sm' variant='primary' className={styles.button}>
              <span className={styles.filterType}>
                {_.get(orderOption, 'label', 'Descending')}
              </span>
              <i className={cx(styles.icon, 'cleverstory-drop-down')} />
            </Button>
          </Dropdown.Toggle>
          <Dropdown.Menu className={styles.typesMenu}>
            {orderOptions.map(item => {
              return <Dropdown.Item
                eventKey={item.value}
                onKeyDown={(e) => handleOrderKeyDown(e, item.value)}>
                {item.label}
              </Dropdown.Item>
            })}
          </Dropdown.Menu>
        </Dropdown>

      </div>
    </div>
  )
}

const renderEmptyMessage = () => {
  return (
    <div className={styles.emptyMessageContainer}>
      <div className={styles.emptyMessage}>
        <video className={styles.emptyImage} loop autoPlay playsInline muted>
          <source src="/images/Blackhole.webm" type="video/webm" />
          <source src="/images/Blackhole.mp4" type="video/mp4" />
        </video>
        <div className={styles.errorMessageContent}>
          <div className={styles.errorMessageTitle}>
            <span>Do<br /> you believe in</span>
            <span>Black<br /> Holes?</span>
          </div>
          <div className={styles.errorMessageSubText}>
            <span>You better do, because nothing<br /> else explains this empty screen.<br /></span>
            <span>...That or you haven’t added <br /> anything here yet.</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ManualManager;
